import React, { useEffect, useState } from "react";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import { t } from "@client/lib/dictionary";
import type { BmsCellInfo } from "@client/types/bms";
import { formatMetric } from "@client/utils/formatMetric";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";

interface Props {
  slug: string;
  cells: BmsCellInfo | undefined;
  cellCount: number;
}

/**
 * EMA (exponential moving average) faktor pro vizuální smoothing výšky barů.
 *
 * BMS reportuje per-cell voltage s drobnou fluktuací (~±5-10 mV) způsobenou
 * interním ADC multiplexem ANT BMS. Bary by jinak skákaly mezi pollingy.
 *
 * alpha = 0.25 ⇒ ~4-tick okno (~6 s při poll 1.5 s), dost agresivní smoothing.
 * Pod každým barem se ale zobrazí ZCELA RAW hodnota z BMS — smoothing je
 * jen vizuální vrstva, čísla zůstanou pravdivá.
 */
const SMOOTHING_ALPHA = 0.25;

/**
 * Stabilní vizualizační měřítko pro bar chart.
 *
 *  - **MIN_DISPLAY_RANGE = 100 mV**: never zobrazujeme menší rozsah, takže ±1 mV
 *    šum (typický pro ANT BMS) neviditelně tancuje na úrovni 1 % výšky baru.
 *  - **RANGE_STEP = 50 mV**: meze jsou snapnuté na grid → 1-2 mV změna avg
 *    nezpůsobí posun celého chartu.
 *  - **PADDING = 60 mV**: aktuální min/max neleží přesně na hranách (extrémy
 *    by viděly 0 / 100 %), ale uvnitř s viditelným rezervou.
 *
 * Pokud delta vyroste přes ~40 mV (delta + padding > 100), rozsah expanduje
 * po 50 mV krocích, aby ukázal větší unbalance — měřítko zůstává smysluplné
 * i pro 200+ mV pack.
 */
const RANGE_STEP_MV = 50;
const MIN_DISPLAY_RANGE_MV = 100;
const PADDING_MV = 60;

const computeDisplayRange = (actualMin: number, actualMax: number) => {
  const actualDelta = actualMax - actualMin;
  const actualCenter = (actualMin + actualMax) / 2;
  // Quantizovaný střed = odolný proti drobným posunům avg
  const quantCenter = Math.round(actualCenter / RANGE_STEP_MV) * RANGE_STEP_MV;
  const rawRange = Math.max(actualDelta + PADDING_MV, MIN_DISPLAY_RANGE_MV);
  // Range zaokrouhlený nahoru na grid → rozšiřuje se po skocích
  const range = Math.ceil(rawRange / RANGE_STEP_MV) * RANGE_STEP_MV;
  return {
    displayMin: quantCenter - range / 2,
    displayMax: quantCenter + range / 2,
    range,
  };
};

/** Vrátí barvu cell baru podle pozice vůči aktuálnímu min/max (ne displayMin/Max). */
const cellColor = (mv: number, min: number, max: number): string => {
  if (max === min) return "bg-blue-500";
  if (mv === max) return "bg-amber-500"; // nejvyšší článek
  if (mv === min) return "bg-rose-500"; // nejnižší článek
  return "bg-blue-500";
};

export const BmsCellGrid: React.FC<Props> = ({ slug, cells, cellCount }) => {
  // HOOKS PRVNĚ — React vyžaduje stejné pořadí v každém renderu. voltages
  // počítáme tady (i pro prázdný stav vrátí []), abychom mohli použít v deps.
  const voltages = cells?.voltagesMv.slice(0, cellCount) ?? [];

  // DB metadata pro souhrny + per-cell (všechny cell-XX mají stejný format mV/0dec)
  const cellAvgMeta = useMetricMeta(`zige/bms-${slug}/cell-avg/val`);
  const cellDeltaMeta = useMetricMeta(`zige/bms-${slug}/cell-delta/val`);
  const cell01Meta = useMetricMeta(`zige/bms-${slug}/cell-01/val`);

  // EMA smoothing — bar visualizace plynule sleduje BMS, BMS jitter neprorazí
  const [smoothedMv, setSmoothedMv] = useState<number[]>([]);
  const voltagesKey = voltages.join("|");
  useEffect(() => {
    if (voltages.length === 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSmoothedMv((prev) => {
      if (prev.length !== voltages.length) return voltages.slice();
      return voltages.map((v, i) => prev[i] * (1 - SMOOTHING_ALPHA) + v * SMOOTHING_ALPHA);
    });
    // voltagesKey postačuje pro re-fire — array identity by triggerovala
    // useEffect na každý render i bez změny hodnot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voltagesKey]);

  if (!cells || voltages.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.bms.cellVoltages}</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground">{t.dashboard.noData}</CardContent>
      </Card>
    );
  }

  const min = Math.min(...voltages);
  const max = Math.max(...voltages);
  const { displayMin, range } = computeDisplayRange(min, max);

  const visualMv = smoothedMv.length === voltages.length ? smoothedMv : voltages;
  // Smoothed min/max → barva (max=amber, min=rose) také nepřeskakuje s každým tickem
  const visualMin = visualMv.length > 0 ? Math.min(...visualMv) : min;
  const visualMax = visualMv.length > 0 ? Math.max(...visualMv) : max;
  return (
    <Card>
      <CardHeader className="flex flex-row items-baseline justify-between gap-4">
        <CardTitle>{t.bms.cellVoltages}</CardTitle>
        <div className="flex gap-4 text-xs text-muted-foreground">
          <span>
            {t.bms.cellAvg}: <strong className="text-foreground">{formatMetric(cells.avgCellMv, cellAvgMeta)}</strong>
          </span>
          <span>
            {t.bms.cellDelta}:{" "}
            <strong className="text-foreground">{formatMetric(cells.cellDeltaMv, cellDeltaMeta)}</strong>
          </span>
          <span>
            {t.bms.cellMin}: <strong className="text-foreground">{formatMetric(min, cell01Meta)}</strong>
          </span>
          <span>
            {t.bms.cellMax}: <strong className="text-foreground">{formatMetric(max, cell01Meta)}</strong>
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
          {voltages.map((mv, idx) => {
            // Vizuální vrstva — smoothed → bar height & color (nestihne přeskočit s každým tickem)
            const visualValue = visualMv[idx] ?? mv;
            const rawPct = ((visualValue - displayMin) / range) * 100;
            const heightPct = Math.max(5, Math.min(95, rawPct));
            const isBalancing = cells.balancingCells?.[idx] === true;
            return (
              <div
                key={idx}
                className="flex flex-col items-center gap-1"
                title={isBalancing ? "Tento článek se právě balancuje" : undefined}
              >
                <div
                  className={`relative w-full h-16 bg-zinc-100 dark:bg-zinc-900 rounded-sm overflow-hidden ${isBalancing ? "ring-2 ring-yellow-400 ring-offset-1 ring-offset-background" : ""}`}
                >
                  <div
                    className={`absolute bottom-0 left-0 right-0 transition-all duration-700 ease-out ${cellColor(visualValue, visualMin, visualMax)}`}
                    style={{ height: `${heightPct}%` }}
                  />
                  {isBalancing && (
                    <span className="absolute top-0.5 right-0.5 text-[8px] font-bold text-yellow-400 bg-zinc-900/80 px-1 rounded">
                      BAL
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-center -mt-1">
                  <span className="text-[10px] text-muted-foreground font-mono">#{idx + 1}</span>
                  {/* Pod barem zobrazujeme RAW hodnotu přímo z BMS — žádné smoothing, žádná manipulace */}
                  <span className="text-xs font-semibold tabular-nums">{formatMetric(mv, cell01Meta)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
